// src/components/account-nav.tsx — shared auth-aware nav links + page header.
//
// There is no single pre-existing header component (landing / projects / new
// each inline their own), so this file provides one AccountNav fragment used
// by all of them, plus an AccountHeader for the account-area pages.

import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { FreeCutLogo } from '@/components/brand/freecut-logo'
import { Button } from '@/components/ui/button'
import { getMeOptional, postLogout, type AuthUser } from '@/lib/kwykedit-api'

export function AccountNav() {
  // undefined = loading, null = signed out
  const [me, setMe] = useState<AuthUser | null | undefined>(undefined)
  const navigate = useNavigate()

  useEffect(() => {
    let live = true
    void getMeOptional()
      .then((user) => {
        if (live) setMe(user)
      })
      .catch(() => {
        // Backend unreachable (e.g. local dev without the API): hide auth
        // links rather than showing a signed-out state we can't verify.
        if (live) setMe(undefined)
      })
    return () => {
      live = false
    }
  }, [])

  if (me === undefined) return null

  if (!me) {
    return (
      <>
        <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
          <Link to="/pricing">Pricing</Link>
        </Button>
        <Button variant="ghost" size="lg" className="gap-2 px-4" asChild>
          <Link to="/login">Log in</Link>
        </Button>
        <Button size="lg" className="gap-2 px-4" asChild>
          <Link to="/signup">Sign up</Link>
        </Button>
      </>
    )
  }

  const handleLogout = () => {
    void postLogout()
      .catch(() => undefined)
      .finally(() => {
        setMe(null)
        void navigate({ to: '/' })
      })
  }

  return (
    <>
      <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
        <Link to="/pricing">Pricing</Link>
      </Button>
      <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
        <Link to="/account">Account</Link>
      </Button>
      {me.is_admin && (
        <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
          <Link to="/admin">Admin</Link>
        </Button>
      )}
      <Button variant="ghost" size="lg" className="gap-2 px-4" onClick={handleLogout}>
        Log out
      </Button>
    </>
  )
}

export function AccountHeader() {
  return (
    <div className="panel-header border-b border-border">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/">
          <FreeCutLogo variant="full" size="md" className="transition-opacity hover:opacity-80" />
        </Link>
        <div className="flex items-center gap-2">
          <AccountNav />
        </div>
      </div>
    </div>
  )
}
